/**
 * ZonaDeCambiosPage.js — Panel oficial de tarifas de remesas (Iter 60 · Jun 2026).
 *
 * Esta página es la **única** fuente de verdad de las tasas que rigen TODA la
 * contabilidad del proyecto Aircom Remesas. CEO y Proveedor CU acceden en
 * paralelo. Lo que se edita acá (colección `usdt_rates_config`) es lo que el
 * motor de operaciones y simulador usan al instante.
 *
 * ⚠️ NO confundir con `/admin/tasas-cambio` (esa pertenece al módulo B2B y NO
 * impacta a las remesas — está aislada del resto del sistema).
 */
import { Card } from '@/components/ui/card';
import { Coins, ShieldCheck, AlertCircle, ArrowUpDown } from 'lucide-react';
import UsdtRatesEditor from '@/components/wallets/UsdtRatesEditor';
import { useAuth } from '@/contexts/AuthContext';

const ZonaDeCambiosPage = () => {
  const { user } = useAuth();
  const role = user?.role;
  const isAdmin = role === 'admin';
  const isProvider = role === 'payment_agent';
  const roleLabel = isAdmin ? 'CEO' : isProvider ? 'Proveedor CU' : '';

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-5" data-testid="zona-de-cambios-page">
      {/* Hero compacto */}
      <Card className="p-6 border-0 shadow-sm bg-gradient-to-br from-emerald-600 to-teal-700 text-white relative overflow-hidden">
        <div className="absolute -right-10 -top-10 opacity-15">
          <Coins className="w-52 h-52" strokeWidth={1} />
        </div>
        <div className="relative">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur px-3 py-1 rounded-full text-xs font-semibold mb-3">
            <ArrowUpDown className="w-3.5 h-3.5" /> Zona de Cambios · Aircom Remesas
          </div>
          <h1 className="text-2xl md:text-3xl font-bold">
            Tarifas oficiales del proyecto
          </h1>
          <p className="text-emerald-50 text-sm mt-2 max-w-2xl">
            Acá editas las tasas que rigen <b>toda</b> la contabilidad de remesas:
            costo y venta de USDT, precios de compra/venta por moneda destino y
            la tarifa directa que recibe el cliente (1 UYU = X CUP/MLC).
            CEO y Proveedor CU comparten este panel en paralelo.
          </p>
          {roleLabel && (
            <p className="text-[11px] uppercase tracking-wider text-emerald-100/80 mt-3">
              Sesión activa: <b>{roleLabel}</b> — tus cambios se aplican al motor al instante.
            </p>
          )}
        </div>
      </Card>

      {/* Recordatorio anti-mezcla */}
      <Card className="p-4 border-l-4 border-amber-400 bg-amber-50/70" data-testid="zona-warning">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-900">
            <p className="font-bold mb-1">No confundir con &quot;Tasas de cambio&quot; del B2B</p>
            <p>
              La página <code className="bg-amber-100 px-1 rounded">/admin/tasas-cambio</code> pertenece al módulo <b>B2B</b> y
              <b> no afecta</b> los cálculos de Aircom Remesas. Para todo lo de remesas — emails al cliente,
              monto recibido por el beneficiario, saldo en wallets, split 50/25/25 — usá esta página.
            </p>
          </div>
        </div>
      </Card>

      {/* Editor */}
      <UsdtRatesEditor />

      {/* Footer educativo */}
      <Card className="p-4 border-0 shadow-sm bg-slate-50" data-testid="zona-footer-help">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
          <div className="text-xs text-slate-700 leading-relaxed">
            <p className="font-bold text-slate-900 mb-1">Cómo funciona el cálculo</p>
            <ul className="list-disc list-inside space-y-1 ml-1">
              <li><b>Tarifa cliente (1 UYU → CUP/MLC)</b>: lo que recibe el beneficiario, sin descuentos. Ej: <code>15.5</code> → 2000 UYU = 31000 CUP exactos.</li>
              <li><b>Costo USDT (UYU)</b>: lo que te cuesta al Proveedor CU comprar 1 USDT.</li>
              <li><b>Venta USDT (UYU)</b>: a cuánto el Proveedor CU le vende 1 USDT al CEO/gestor.</li>
              <li>El <b>margen Aircom</b> vive en el spread compra/venta — totalmente invisible al cliente.</li>
              <li>El reparto <b>50% Proveedor · 25% CEO · 25% Gestor</b> se calcula sobre el spread cuando la remesa pasa a <code>DELIVERED</code>.</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ZonaDeCambiosPage;
